import { useEffect, useRef, useState } from 'react';
import './FilterSelect.css';

/**
 * Custom select — option menu stays within parent width on mobile.
 * Native <select> popups often overflow in Chrome device mode.
 */
export default function FilterSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  className = '',
  size = 'md', // md | sm
  variant = 'default', // default | light | bordered
  menuAlign = 'start', // start | end
  'aria-label': ariaLabel = 'Select',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const label = selected?.label ?? placeholder;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const rootClass = [
    'filter-select',
    open ? 'is-open' : '',
    size === 'sm' ? 'filter-select--sm' : '',
    variant === 'light' ? 'filter-select--light' : '',
    variant === 'bordered' ? 'filter-select--bordered' : '',
    menuAlign === 'end' ? 'filter-select--end' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} ref={rootRef}>
      <button
        type="button"
        className="filter-select-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="filter-select-label text-truncate">{label}</span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} />
      </button>
      {open && (
        <ul className="filter-select-menu" role="listbox">
          {options.map((opt) => (
            <li key={String(opt.value)}>
              <button
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={`filter-select-option ${opt.value === value ? 'is-active' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
